import { SetupTaskId } from '../setupTasksData'
import { SetupStep } from './d'
import { $RootStore } from '../../../../src/stores/RootStore'
import { CampaignStatus, CampaignType, LoyaltySchemeStatus } from '../../../../graphQL/generated/enums'

export const MarketingToolsStepIds = {
  promocode: `${SetupTaskId.MarketingTools}-promocode`,
  loyaltyScheme: `${SetupTaskId.MarketingTools}-loyaltyScheme`,
  referralScheme: `${SetupTaskId.MarketingTools}-referralScheme`,
  automatedMessages: `${SetupTaskId.MarketingTools}-automatedMessages`,
  broadcastMessages: `${SetupTaskId.MarketingTools}-broadcastMessages`,
} as const

const marketingToolsSteps: SetupStep[] = [
  {
    title: 'Promo codes',
    description: 'Create discounts to drive bookings during quieter periods or special campaigns.',
    id: MarketingToolsStepIds.promocode,
    taskId: SetupTaskId.MarketingTools,
    icon: 'promocode',
    check: (rootStore: $RootStore) => {
      return rootStore.externalPromocodeStore.items.length > 0
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      await rootStore.communication.requester.deleteSaasData({ models: ['ExternalPromoCode'] })
    },
    toastData: {
      title: 'Your first promo code has been created',
      nextStepId: SetupTaskId.MarketingTools,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Loyalty scheme',
    description: 'Reward repeat clients to increase retention and lifetime value.',
    id: MarketingToolsStepIds.loyaltyScheme,
    taskId: SetupTaskId.MarketingTools,
    icon: 'loyalty',
    check: (rootStore: $RootStore) => {
      return rootStore.setupStore.didAction === 'enabledLoyaltyScheme'
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      const scheme = rootStore.userStore.defaultLoyaltyScheme
      if (scheme) {
        scheme.status = LoyaltySchemeStatus.Draft
        await rootStore.communication.requester.updateLoyaltyScheme({ scheme })
      }
    },
    toastData: {
      title: 'Your have enabled your loyalty scheme',
      nextStepId: SetupTaskId.MarketingTools,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Client referral scheme',
    description: 'Encourage word-of-mouth by rewarding clients who refer their friends.',
    id: MarketingToolsStepIds.referralScheme,
    taskId: SetupTaskId.MarketingTools,
    icon: 'gift',
    check: (rootStore: $RootStore) => {
      return rootStore.setupStore.didAction === 'enabledReferralScheme'
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      const scheme = rootStore.userStore.clientReferralScheme
      if (scheme) {
        scheme.status = LoyaltySchemeStatus.Draft
        await rootStore.communication.requester.updateLoyaltyScheme({ scheme })
      }
    },
    toastData: {
      title: 'You have enabled a referral scheme for your clients.',
      nextStepId: SetupTaskId.MarketingTools,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Automated messages',
    description: 'Send targeted messages automatically to re-engage clients and boost re-bookings.',
    id: MarketingToolsStepIds.automatedMessages,
    taskId: SetupTaskId.MarketingTools,
    icon: 'email',
    check: (rootStore: $RootStore) => {
      return rootStore.emailCampaignStore.items.some(
        item => item.data.type === CampaignType.Automated && item.data.status === CampaignStatus.Active,
      )
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      const emails = rootStore.emailCampaignStore.items.filter(
        item => item.data.type === CampaignType.Automated && item.data.status === CampaignStatus.Active,
      )
      await Promise.all(emails.map(email =>
        rootStore.emailCampaignStore.updateEmailCampaign_api({ ...email.data, status: CampaignStatus.Draft }),
      ))
    },
    toastData: {
      title: 'You set up automated messages for your clients',
      nextStepId: SetupTaskId.MarketingTools,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Broadcast messages',
    description: 'Send one-off updates or promotions to your client list in seconds.',
    id: MarketingToolsStepIds.broadcastMessages,
    taskId: SetupTaskId.MarketingTools,
    icon: 'broadcastEmails',
    check: (rootStore: $RootStore) => {
      return rootStore.emailCampaignStore.items.some(
        item => item.data.type === CampaignType.Broadcast
          && (item.data.status === CampaignStatus.Sent || item.data.status === CampaignStatus.Scheduled),
      )
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      const { emailCampaignStore } = rootStore
      const emails = emailCampaignStore.items.filter(
        item => item.data.type === CampaignType.Broadcast
          && (item.data.status === CampaignStatus.Sent || item.data.status === CampaignStatus.Scheduled),
      )
      await Promise.all(emails.map(email =>
        emailCampaignStore.updateEmailCampaign_api({ ...email.data, status: CampaignStatus.Archived }),
      ))
    },
    toastData: {
      title: 'You sent a broadcast message to your clients',
      nextStepId: SetupTaskId.MarketingTools,
    },
    dependsOnStepIds: [],
  },
]

export default marketingToolsSteps
